import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import type { Instrument, TowerTarget } from './tower-model';

// INSTRUMENTS — one row, six cells, ONE source (resolution-state.kpis). Replaces
// the header stats + the facts line + the roster tiles of the classic overview.
// Value 20px tabular · label-caps · a 4px three-tone bar (solid / hatched / warn).

export function TowerInstruments({
  items,
  onTarget,
}: {
  items: Instrument[];
  onTarget: (t: TowerTarget) => void;
}) {
  const { t } = useTranslation('tower');
  return (
    <div
      className="grid shrink-0 grid-cols-6 border-b border-border bg-background"
      data-testid="tower-instruments"
    >
      {items.map((it) => {
        const Comp = it.target ? 'button' : 'div';
        return (
          <Comp
            key={it.key}
            type={it.target ? 'button' : undefined}
            onClick={it.target ? () => onTarget(it.target!) : undefined}
            className={cn(
              'grid min-w-0 gap-0.5 border-r border-border px-4 py-2 text-left last:border-r-0',
              it.target && 'hover:bg-muted/40',
            )}
            data-testid={`tower-instrument-${it.key}`}
          >
            <span
              className={cn(
                'truncate text-xl font-bold tabular-nums leading-tight tracking-tight text-foreground',
                it.hatchedValue &&
                  'underline decoration-primary/60 decoration-dotted underline-offset-4',
              )}
              title={it.hatchedValue ? t('instruments.reversibleTitle') : undefined}
            >
              {it.value}
            </span>
            <span className="truncate text-[11px] text-muted-foreground">
              {it.small ?? '\u00a0'}
            </span>
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {it.label}
            </span>
            <div className="mt-0.5 flex h-1 overflow-hidden rounded-full bg-muted" aria-hidden>
              <i className="block h-full bg-primary" style={{ width: `${it.bar[0]}%` }} />
              <i
                className="block h-full opacity-80"
                style={{
                  width: `${it.bar[1]}%`,
                  backgroundImage:
                    'repeating-linear-gradient(135deg, hsl(var(--primary)) 0 2px, transparent 2px 4px)',
                }}
              />
              <i
                className="block h-full"
                style={{ width: `${it.bar[2]}%`, background: 'var(--status-warn, #C27C08)' }}
              />
            </div>
          </Comp>
        );
      })}
    </div>
  );
}
